<template>
    <TriadModal ref="modal" :title="$t('songBatchImport.modalTitle')" size="large" @close="handleClose">
        <div class="space-y-4">
            <!-- Instructions and Limits info -->
            <div class="bg-indigo-50/70 dark:bg-indigo-950/30 border border-indigo-100 dark:border-indigo-900/50 rounded-xl p-3.5 flex items-start gap-3">
                <div class="p-1.5 bg-indigo-100 dark:bg-indigo-900/50 text-indigo-600 dark:text-indigo-400 rounded-lg shrink-0 mt-0.5">
                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                </div>
                <div class="text-xs sm:text-sm text-gray-700 dark:text-gray-300">
                    <p class="font-semibold text-gray-900 dark:text-white mb-0.5">{{ $t('songBatchImport.infoTitle') }}</p>
                    <p>{{ $t('songBatchImport.infoDesc') }}</p>
                </div>
            </div>

            <!-- Upload Dropzone (visible when not processing and not finished) -->
            <div 
                v-if="!processing && !importResult"
                @dragover.prevent="isDragging = true"
                @dragleave.prevent="isDragging = false"
                @drop.prevent="handleDrop"
                class="border-2 border-dashed rounded-2xl p-6 sm:p-8 text-center transition-all duration-200 cursor-pointer flex flex-col items-center justify-center gap-3"
                :class="isDragging ? 'border-indigo-500 bg-indigo-50/50 dark:bg-indigo-950/40' : 'border-gray-200 dark:border-gray-700 hover:border-indigo-400 dark:hover:border-indigo-500 bg-gray-50/50 dark:bg-gray-800/50'"
                @click="triggerFileInput"
            >
                <input 
                    ref="fileInputRef"
                    type="file" 
                    accept=".txt,text/plain" 
                    class="hidden" 
                    @change="handleFileSelect"
                />

                <div class="p-3 bg-indigo-100 dark:bg-indigo-900/50 text-indigo-600 dark:text-indigo-400 rounded-2xl">
                    <svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                </div>

                <div class="space-y-1">
                    <p class="text-sm font-semibold text-gray-800 dark:text-gray-200">
                        {{ selectedFile ? selectedFile.name : $t('songBatchImport.dragPrompt') }}
                    </p>
                    <p class="text-xs text-gray-500 dark:text-gray-400">
                        {{ selectedFile ? formatFileSize(selectedFile.size) : $t('songBatchImport.formatHint') }}
                    </p>
                </div>

                <div v-if="selectedFile" class="pt-2 flex flex-col items-center gap-3" @click.stop>
                    <label class="inline-flex items-center gap-2 cursor-pointer select-none text-xs sm:text-sm text-gray-700 dark:text-gray-300" @click.stop>
                        <input 
                            type="checkbox" 
                            v-model="overwriteExisting" 
                            class="rounded border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500 w-4 h-4 cursor-pointer"
                            @click.stop
                        />
                        <span>{{ $t('songBatchImport.overwriteLabel') }}</span>
                    </label>

                    <div class="flex items-center gap-2" @click.stop>
                        <button 
                            type="button" 
                            @click.stop="startUpload" 
                            class="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 active:bg-indigo-800 text-white text-xs sm:text-sm font-semibold rounded-lg shadow-sm transition-all"
                        >
                            {{ $t('songBatchImport.startImportBtn') }}
                        </button>
                        <button 
                            type="button" 
                            @click.stop="clearSelectedFile" 
                            class="px-3 py-2 border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 text-xs sm:text-sm font-medium rounded-lg"
                        >
                            {{ $t('songBatchImport.clearBtn') }}
                        </button>
                    </div>
                </div>
            </div>

            <!-- Processing Spinner State -->
            <div v-if="processing" class="py-10 text-center space-y-4">
                <div class="inline-flex items-center justify-center p-3 bg-indigo-50 dark:bg-indigo-950/60 rounded-full text-indigo-600 dark:text-indigo-400 animate-spin">
                    <svg class="w-8 h-8" fill="none" viewBox="0 0 24 24">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
                    </svg>
                </div>
                <div>
                    <h4 class="text-base font-semibold text-gray-900 dark:text-white">{{ $t('songBatchImport.processingTitle') }}</h4>
                    <p class="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-1 max-w-md mx-auto">
                        {{ $t('songBatchImport.processingDesc') }}
                    </p>
                </div>
            </div>

            <!-- Import Summary & Results List -->
            <div v-if="importResult" class="space-y-4">
                <!-- Metrics Bar -->
                <div class="grid grid-cols-2 sm:grid-cols-5 gap-2.5">
                    <div class="bg-gray-50 dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-xl p-3 text-center">
                        <span class="text-xs text-gray-500 dark:text-gray-400 block">{{ $t('songBatchImport.totalParsed') }}</span>
                        <span class="text-lg font-bold text-gray-900 dark:text-white">{{ importResult.total_parsed || 0 }}</span>
                    </div>
                    <div class="bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-100 dark:border-emerald-900/50 rounded-xl p-3 text-center">
                        <span class="text-xs text-emerald-700 dark:text-emerald-400 block">{{ $t('songBatchImport.createdCount') }}</span>
                        <span class="text-lg font-bold text-emerald-600 dark:text-emerald-400">{{ importResult.created || 0 }}</span>
                    </div>
                    <div class="bg-blue-50 dark:bg-blue-950/30 border border-blue-100 dark:border-blue-900/50 rounded-xl p-3 text-center">
                        <span class="text-xs text-blue-700 dark:text-blue-400 block">{{ $t('songBatchImport.updatedCount') }}</span>
                        <span class="text-lg font-bold text-blue-600 dark:text-blue-400">{{ importResult.updated || 0 }}</span>
                    </div>
                    <div class="bg-amber-50 dark:bg-amber-950/30 border border-amber-100 dark:border-amber-900/50 rounded-xl p-3 text-center">
                        <span class="text-xs text-amber-700 dark:text-amber-400 block">{{ $t('songBatchImport.skippedCount') }}</span>
                        <span class="text-lg font-bold text-amber-600 dark:text-amber-400">{{ importResult.skipped || 0 }}</span>
                    </div>
                    <div class="bg-red-50 dark:bg-red-950/30 border border-red-100 dark:border-red-900/50 rounded-xl p-3 text-center">
                        <span class="text-xs text-red-700 dark:text-red-400 block">{{ $t('songBatchImport.failedCount') }}</span>
                        <span class="text-lg font-bold text-red-600 dark:text-red-400">{{ importResult.failed || 0 }}</span>
                    </div>
                </div>

                <!-- Detailed Song Item List -->
                <div class="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden max-h-[360px] overflow-y-auto divide-y divide-gray-100 dark:divide-gray-700/60">
                    <div 
                        v-for="item in importResult.songs" 
                        :key="item.index"
                        class="p-3 sm:p-3.5 flex flex-col sm:flex-row sm:items-center justify-between gap-2 transition-colors hover:bg-gray-50/50 dark:hover:bg-gray-750/30"
                    >
                        <div class="min-w-0 flex-1">
                            <div class="flex items-center gap-2 flex-wrap">
                                <span class="font-semibold text-sm text-gray-900 dark:text-white truncate">
                                    {{ item.title || $t('songBatchImport.unnamedSong') }}
                                </span>
                                <span class="text-xs text-gray-500 dark:text-gray-400">
                                    • {{ item.artist || $t('songBatchImport.unknownArtist') }}
                                </span>
                                <span v-if="item.artist_created" class="px-1.5 py-0.5 text-[10px] font-semibold bg-blue-50 text-blue-700 border border-blue-200 dark:bg-blue-950/40 dark:text-blue-300 rounded">
                                    {{ $t('songBatchImport.newArtist') }}
                                </span>
                                <span v-if="item.key" class="px-1.5 py-0.5 text-[10px] font-mono font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded">
                                    {{ item.key }}
                                </span>
                            </div>

                            <!-- Tags Badges -->
                            <div v-if="item.tags && item.tags.length > 0" class="flex flex-wrap items-center gap-1.5 mt-1.5">
                                <span 
                                    v-for="tag in item.tags" 
                                    :key="tag"
                                    class="px-1.5 py-0.5 text-[10px] rounded bg-indigo-50 dark:bg-indigo-950/40 text-indigo-600 dark:text-indigo-400 border border-indigo-100 dark:border-indigo-900/50"
                                >
                                    {{ tag }}
                                </span>
                            </div>

                            <p v-if="item.message && item.status !== 'created'" class="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                {{ item.message }}
                            </p>
                        </div>

                        <!-- Status Badge -->
                        <div class="shrink-0 flex items-center gap-1.5 self-start sm:self-center">
                            <span 
                                v-if="item.status === 'created'" 
                                class="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold rounded-full bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300"
                            >
                                <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                                </svg>
                                {{ $t('songBatchImport.statusCreated') }}
                            </span>
                            <span 
                                v-else-if="item.status === 'updated'" 
                                class="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800 dark:bg-blue-950/60 dark:text-blue-300"
                            >
                                <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                </svg>
                                {{ $t('songBatchImport.statusUpdated') }}
                            </span>
                            <span 
                                v-else-if="item.status === 'skipped'" 
                                class="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold rounded-full bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300"
                            >
                                <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                                </svg>
                                {{ $t('songBatchImport.statusSkipped') }}
                            </span>
                            <span 
                                v-else 
                                class="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800 dark:bg-red-950/60 dark:text-red-300"
                            >
                                <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                                {{ $t('songBatchImport.statusFailed') }}
                            </span>
                        </div>
                    </div>
                </div>

                <!-- Actions after import -->
                <div class="pt-2 flex items-center justify-end gap-2">
                    <button 
                        type="button" 
                        @click="resetAndImportAnother" 
                        class="px-3.5 py-2 border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 text-xs sm:text-sm font-medium rounded-lg"
                    >
                        {{ $t('songBatchImport.importAnotherBtn') }}
                    </button>
                    <button 
                        type="button" 
                        @click="closeModal" 
                        class="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-xs sm:text-sm font-semibold rounded-lg shadow-sm"
                    >
                        {{ $t('songBatchImport.doneBtn') }}
                    </button>
                </div>
            </div>
        </div>
    </TriadModal>
</template>

<script setup>
import { ref, defineEmits, defineExpose } from 'vue';
import { useI18n } from 'vue-i18n';
import TriadModal from '@/components/common/TriadModal.vue';
import toastStore from '@/store/toast.store';

const { t } = useI18n();
const emit = defineEmits(['imported']);

const modal = ref(null);
const fileInputRef = ref(null);
const isDragging = ref(false);
const selectedFile = ref(null);
const overwriteExisting = ref(false);
const processing = ref(false);
const importResult = ref(null);

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

const open = () => {
    selectedFile.value = null;
    overwriteExisting.value = false;
    processing.value = false;
    importResult.value = null;
    isDragging.value = false;
    modal.value?.open();
};

const closeModal = () => {
    modal.value?.close();
};

const handleClose = () => {
    if (importResult.value && (importResult.value.created > 0 || importResult.value.updated > 0)) {
        emit('imported');
    }
};

const triggerFileInput = () => {
    fileInputRef.value?.click();
};

const handleFileSelect = (event) => {
    const file = event.target.files?.[0];
    validateAndSetFile(file);
};

const handleDrop = (event) => {
    isDragging.value = false;
    const file = event.dataTransfer?.files?.[0];
    validateAndSetFile(file);
};

const validateAndSetFile = (file) => {
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.txt')) {
        toastStore.dispatch('addMessage', {
            message: t('songBatchImport.invalidExtensionError'),
            icon: 'warning',
            ttl: 4000
        });
        return;
    }

    if (file.size > MAX_FILE_SIZE) {
        toastStore.dispatch('addMessage', {
            message: t('songBatchImport.fileTooLargeError'),
            icon: 'warning',
            ttl: 4000
        });
        return;
    }

    selectedFile.value = file;
};

const clearSelectedFile = () => {
    selectedFile.value = null;
    if (fileInputRef.value) {
        fileInputRef.value.value = '';
    }
};

const resetAndImportAnother = () => {
    clearSelectedFile();
    importResult.value = null;
    processing.value = false;
};

const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

const startUpload = async () => {
    if (!selectedFile.value) return;

    processing.value = true;
    importResult.value = null;

    try {
        const fileText = await selectedFile.value.text();

        const response = await fetch('/api/song/batch-import', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                content: fileText,
                overwrite: overwriteExisting.value
            })
        });

        let data;
        const contentType = response.headers.get('content-type') || '';
        if (contentType.includes('application/json')) {
            data = await response.json();
        } else {
            const rawText = await response.text();
            throw new Error(rawText.slice(0, 150) || `HTTP error ${response.status}`);
        }

        if (response.ok && data) {
            importResult.value = data;
            const createdCount = data.created || 0;
            const updatedCount = data.updated || 0;
            if (createdCount > 0 || updatedCount > 0) {
                toastStore.dispatch('addMessage', {
                    message: t('songBatchImport.successToast', { count: createdCount + updatedCount }),
                    icon: 'success',
                    ttl: 4000
                });
                emit('imported');
            } else if (data.skipped > 0 && createdCount === 0 && updatedCount === 0) {
                toastStore.dispatch('addMessage', {
                    message: t('songBatchImport.allSkippedToast'),
                    icon: 'warning',
                    ttl: 4000
                });
            }
        } else {
            const errorMsg = (data && (data.message || data.error)) || (typeof data?.error === 'string' ? data.error : null) || t('songBatchImport.importFailedError');
            toastStore.dispatch('addMessage', {
                message: String(errorMsg),
                icon: 'error',
                ttl: 5000
            });
        }
    } catch (err) {
        toastStore.dispatch('addMessage', {
            message: String(err.message || t('songBatchImport.networkError')),
            icon: 'error',
            ttl: 5000
        });
    } finally {
        processing.value = false;
    }
};

defineExpose({
    open,
    close: closeModal
});
</script>
